import { useState, useEffect, useMemo, useRef, useCallback, type MouseEvent as ReactMouseEvent } from 'react';
import { createPortal } from 'react-dom';
import { Incident, type TriageSummary } from '../data/demoData';
import { formatTimeAgo, sentinelIncidentNumber } from '../lib/utils';
import { IncidentModal } from './IncidentModal';
import { CloseIncidentDialog, type ClosePayload } from './CloseIncidentDialog';
import { IncidentStatusDialog, type IncidentStatusPayload } from './IncidentStatusDialog';
import { IncidentSeverityDialog, type IncidentSeverityPayload } from './IncidentSeverityDialog';
import { IncidentAssignDialog, type IncidentAssignPayload } from './IncidentAssignDialog';
import { DeepInvestigationDrawer } from './DeepInvestigationDrawer';
import { ApiError, apiFetch } from '../lib/apiClient';
import { useQueries } from '@tanstack/react-query';
import { useCustomers } from '../context/CustomerContext';
import { SlaBreachBadge } from './SlaBreachBadge';
import { FilterableColumnTable, type FilterableColumnDef } from './FilterableColumnTable';
import { Breadcrumb, BreadcrumbList, BreadcrumbItem, BreadcrumbLink, BreadcrumbPage, BreadcrumbSeparator } from './ui/breadcrumb';
import { useAuth } from '../context/AuthContext';
import { fmtTs } from '../lib/globalTz';
import { notifyIncidentExecutionUpdated } from '../lib/incidentExecutionEvents';
import { GuardianOperationsPanel } from './GuardianOperationsPanel';

interface TenantUser {
  displayName: string;
  userPrincipalName: string;
  email?: string;
  role?: string;
  source?: string;
  canAssign?: boolean;
  autoAssign?: boolean;
  severityScopes?: string[];
  lighthouseVerified?: boolean;
  reason?: string | null;
}

interface MlIncidentScoreRow {
  customerId: string;
  incidentId: string;
  incidentNumber: string | null;
  title: string;
  mlPriorityScore: number;
  falsePositiveLikelihood: number;
  confidence: number;
  explanation: string[];
  recommendedAction: string | null;
  updatedAt: string | null;
}

type TriageQueueCategory = 'Critical' | 'Suspicious' | 'Routine' | 'Informational';

interface TriageQueueScoredIncident {
  id: string;
  score: number;
  category: TriageQueueCategory;
  reasons: string[];
}

interface TriageQueueScoreResponse {
  summary: { total: number; Critical: number; Suspicious: number; Routine: number; Informational: number };
  scored: TriageQueueScoredIncident[];
}

const TRIAGE_QUEUE_CATEGORY_CLASS: Record<TriageQueueCategory, string> = {
  Critical: 'bg-red-500/15 text-red-700 dark:text-red-300 border-red-500/40',
  Suspicious: 'bg-orange-500/15 text-orange-700 dark:text-orange-300 border-orange-500/40',
  Routine: 'bg-blue-500/15 text-blue-700 dark:text-blue-300 border-blue-500/40',
  Informational: 'bg-gray-500/15 text-gray-700 dark:text-gray-300 border-gray-500/40',
};

function effectiveStatus(i: Incident) {
  const s = (i.status || '').toLowerCase();
  // Sentinel status and owner are separate fields. Do not infer New from an
  // empty owner; Active/In Progress can be unassigned or can take a moment to
  // show an owner after a delegated Graph providerIncidentId update.
  return s;
}

function getStatusClass(i: Incident) {
  const s = effectiveStatus(i);
  if (s === 'new') return 'status-new';
  if (s === 'active') return 'status-active';
  if (s === 'closed') return 'status-closed';
  if (s === 'redirected') return 'status-redirected';
  return '';
}

function getStatusLabel(i: Incident) {
  const s = effectiveStatus(i);
  if (s === 'new') return 'New';
  if (s === 'active') return 'In Progress';
  if (s === 'closed') return 'Resolved';
  if (s === 'redirected') return 'Redirected';
  return i.status;
}

function getSeverityClass(sev: string) {
  if (sev === 'High') return 'severity-high';
  if (sev === 'Medium') return 'severity-medium';
  if (sev === 'Low') return 'severity-low';
  return 'severity-informational';
}


function getProviderLabel(i: Incident): string {
  const raw = (i.providerName || i.providerProductName || '').trim();
  if (!raw) return 'Microsoft Sentinel';
  const lower = raw.toLowerCase();
  if (lower.includes('xdr') || lower.includes('defender') || lower.includes('microsoft 365')) return 'Microsoft Defender XDR';
  if (lower.includes('sentinel') || lower.includes('securityinsights')) return 'Microsoft Sentinel';
  return raw;
}

function getProviderClass(provider: string): string {
  const p = provider.toLowerCase();
  if (p.includes('defender') || p.includes('xdr')) return 'bg-purple-100 text-purple-700 border-purple-200 dark:bg-purple-950 dark:text-purple-300 dark:border-purple-800';
  if (p.includes('sentinel')) return 'bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-950 dark:text-blue-300 dark:border-blue-800';
  if (p.includes('azure')) return 'bg-sky-100 text-sky-700 border-sky-200 dark:bg-sky-950 dark:text-sky-300 dark:border-sky-800';
  return 'bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-900 dark:text-slate-300 dark:border-slate-700';
}

function getAlertProviderLabels(i: Incident): string[] {
  const values = Array.isArray(i.alertProviderNames) ? i.alertProviderNames : [];
  const cleaned = values
    .map((value) => String(value || '').replace(/\s+/g, ' ').trim())
    .filter(Boolean);

  if (cleaned.length === 0) {
    const fallback = String(i.alertProviderName || i.providerProductName || '').replace(/\s+/g, ' ').trim();
    return fallback ? [fallback] : [];
  }

  return Array.from(new Set(cleaned)).slice(0, 3);
}

/**
 * INCONCLUSIVE rows older than this are flagged as "stale" in the chip so
 * analysts know the model's "wait_for_more_signal" recommendation has been
 * sitting unmoved for a while and a manual re-triage is probably warranted.
 * Mirrors the value used by support docs; tune in lockstep if the auto-retry
 * follow-up (item B in the design memo) ever ships server-side.
 */
const INCONCLUSIVE_STALE_MS = 30 * 60_000;

/**
 * Compact verdict chip for the Autonomous Sentinel-Incident Triage Agent.
 * Renders next to the incident title so an L1 analyst can immediately see
 * the AI's preliminary verdict + confidence and hover for the supporting
 * summary, evidence, and recommended next action. `null` (no triage row
 * yet - the customer hasn't opted in or the worker hasn't run yet) renders
 * nothing so we don't flash empty state on every poll.
 *
 * INCONCLUSIVE handling: previously the chip showed "KN AISOC: ? · 40%"
 * which read like a progress indicator. INCONCLUSIVE now renders as
 * "AI: insufficient signal" with the model's confidence labelled
 * explicitly ("AI conf 40%") and the tooltip spells out that the system
 * does NOT auto-poll - re-triage only fires when the upstream incident
 * mutates (new alert / status / lastUpdateTime) or when the analyst hits
 * the Re-triage button rendered next to the chip.
 */
function TriageVerdictBadge({ triage }: { triage: TriageSummary | null }) {
  if (!triage) return null;
  // Show a muted pending pill while the worker is still running so analysts
  // know triage is in flight and won't be surprised by a sudden auto-close.
  if (triage.status === 'pending' || triage.status === 'running') {
    return (
      <span
        className="ml-1.5 inline-block px-1.5 py-0.5 text-[9px] font-bold rounded border align-middle bg-slate-500/15 text-slate-600 dark:text-slate-300 border-slate-500/30"
        title="Autonomous triage in progress"
      >
        AI…
      </span>
    );
  }
  if (triage.status === 'failed') {
    return (
      <span
        className="ml-1.5 inline-block px-1.5 py-0.5 text-[9px] font-bold rounded border align-middle bg-red-500/15 text-red-700 dark:text-red-300 border-red-500/40"
        title={`Triage failed: ${triage.error ?? 'unknown error'}`}
      >
        AI ERR
      </span>
    );
  }
  const v = triage.verdict;
  if (!v) return null;

  const isInconclusive = v.verdict === 'INCONCLUSIVE';

  // Verdict-driven palette: TRUE POSITIVE = red (real attack), FALSE POSITIVE
  // = green (analyst can safely dismiss), BENIGN POSITIVE = sky blue
  // (real activity but expected - e.g. authorized pen-test), INCONCLUSIVE
  // = amber (model couldn't call it - analyst attention needed).
  const cls =
    v.verdict === 'TRUE POSITIVE'
      ? 'bg-red-500/20 text-red-700 dark:text-red-300 border-red-500/40'
      : v.verdict === 'FALSE POSITIVE'
        ? 'bg-emerald-500/20 text-emerald-700 dark:text-emerald-300 border-emerald-500/40'
        : v.verdict === 'BENIGN POSITIVE'
          ? 'bg-sky-500/20 text-sky-700 dark:text-sky-300 border-sky-500/40'
          : 'bg-amber-500/20 text-amber-700 dark:text-amber-300 border-amber-500/40';

  const shortVerdict =
    v.verdict === 'TRUE POSITIVE'
      ? 'TP'
      : v.verdict === 'FALSE POSITIVE'
        ? 'FP'
        : v.verdict === 'BENIGN POSITIVE'
          ? 'BP'
          : 'insufficient signal';

  // For INCONCLUSIVE, flag the row as "stale" once the recommendation has
  // been sitting for >30 min without an upstream mutation triggering a
  // re-triage. Surfaced as a small "·stale" tail and a more urgent tooltip.
  const finishedAtMs = triage.finishedAt ? new Date(triage.finishedAt).getTime() : null;
  const isStale =
    isInconclusive &&
    finishedAtMs !== null &&
    Date.now() - finishedAtMs > INCONCLUSIVE_STALE_MS;

  // Multi-line tooltip showing summary + top evidence + recommendation. The
  // browser-native `title` is intentional - keeps the chip lightweight and
  // accessible without dragging in a popover lib for the inline list view.
  // The IncidentModal renders the full evidence panel.
  const evidenceLines = (v.keyEvidence ?? [])
    .slice(0, 5)
    .map((e) => `• [${e.kind}] ${e.label}${e.detail ? ` - ${e.detail}` : ''}`)
    .join('\n');
  const closedSuffix =
    triage.autoClosed && triage.closedClassification
      ? `\n\nAuto-closed as ${triage.closedClassification}.`
      : '';

  // INCONCLUSIVE gets a bespoke tooltip that explicitly names the
  // re-triage trigger (upstream mutation OR analyst-driven Re-triage
  // button) so analysts don't sit waiting for a watchdog that doesn't
  // exist. Definitive verdicts keep the original verdict-first format.
  const tooltip = isInconclusive
    ? `KN AISOC: insufficient signal (AI confidence ${v.confidence}%)\n\n` +
      `${v.summary}\n\n` +
      (evidenceLines ? `Evidence the agent found:\n${evidenceLines}\n\n` : '') +
      `Recommended: ${v.recommendedAction.replace(/_/g, ' ')}\n\n` +
      `Note: re-triage runs automatically only when Sentinel updates this incident ` +
      `(new correlated alert, status change, new comment). It does NOT poll on a ` +
      `timer. Click the Re-triage button (↻) next to this chip to force a fresh ` +
      `run with the latest evidence pack now.` +
      (isStale
        ? `\n\nThis verdict is older than 30 min - fresh signal may now be available.`
        : '') +
      closedSuffix
    : `KN AISOC Verdict: ${v.verdict} (${v.confidence}% confidence)\n` +
      `Recommended: ${v.recommendedAction.replace(/_/g, ' ')}\n\n` +
      `${v.summary}\n\n` +
      (evidenceLines ? `Key evidence:\n${evidenceLines}` : '') +
      closedSuffix;

  return (
    <span
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] font-bold rounded border ${cls}`}
      title={tooltip}
    >
      <span>KN AISOC: {shortVerdict}</span>
      <span className="opacity-80">
        {isInconclusive ? `· AI conf ${v.confidence}%` : `· ${v.confidence}%`}
      </span>
      {isStale && (
        <span
          className="ml-0.5 px-1 rounded bg-amber-500/30 text-[8px] uppercase tracking-wide"
          title="No fresh signal received in the last 30 min - consider a manual re-triage"
        >
          stale
        </span>
      )}
      {triage.autoClosed && (
        <span
          className="ml-0.5 px-1 rounded bg-foreground/10 text-[8px]"
          title="Closed automatically by the triage agent"
        >
          AUTO
        </span>
      )}
    </span>
  );
}

/**
 * Inline "↻ Re-triage" button rendered next to the verdict chip. Lets the
 * analyst force a fresh triage run when they suspect new signal has landed
 * (a fresh sign-in event, a new correlated alert, etc) and they don't want
 * to wait for the next signature-driven re-run.
 *
 * Hits POST /incidents/:id/triage with `force: true`. The server is
 * gated by `requireTabWrite("incidents")` + `aiHeavyLimiter` so we mirror
 * the same gate client-side via the parent's `readOnly` (derived from
 * `canWrite('incidents', customerId)`). Available to all writer roles -
 * triage is a single AOAI call, much cheaper than Deep Investigate, so
 * we don't restrict L1 here.
 *
 * The dashboard's regular poll picks up the new triage row on its next
 * tick (≤30s) so we don't need to re-fetch ourselves - we just flip a
 * local "running" indicator until either the prop's `triage.status`
 * advances past pending/running or the request settles.
 */
function ReTriageButton({
  incidentId,
  customerId,
  triage,
  disabled,
}: {
  incidentId: string;
  customerId: string;
  triage: TriageSummary | null;
  disabled: boolean;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Only show the button when there's something to re-trigger from: a
  // completed verdict the analyst wants to refresh, OR a failed attempt
  // the analyst wants to retry. Hide while the auto-triage scheduler is
  // already in flight (pending/running) so a manual click can't race it,
  // and hide when no row exists yet (no verdict, no error - the scheduler
  // either hasn't kicked off or this customer hasn't opted in).
  if (!triage) return null;
  if (triage.status === 'pending' || triage.status === 'running') return null;
  if (!triage.verdict && triage.status !== 'failed') return null;
  const onClick = async (e: ReactMouseEvent) => {
    e.stopPropagation();
    if (busy || disabled) return;
    setBusy(true);
    setError(null);
    try {
      await apiFetch(`/incidents/${encodeURIComponent(incidentId)}/triage`, {
        method: 'POST',
        body: JSON.stringify({ customerId, force: true }),
      });
      // The dashboard's React Query poll will refresh `incident.triage`
      // within the next tick. We keep `busy` set briefly so the analyst
      // gets visual feedback that the click registered.
      setTimeout(() => setBusy(false), 1500);
    } catch (err) {
      setBusy(false);
      setError(err instanceof Error ? err.message : 'Re-triage failed');
    }
  };
  if (disabled) return null;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      title={
        error
          ? `Re-triage failed: ${error}. Click to retry.`
          : busy
            ? 'Re-triaging - fresh verdict will appear on next poll'
            : 'Re-run AI triage with the latest evidence pack'
      }
      className={`inline-flex items-center justify-center w-5 h-5 rounded border text-[10px] transition-colors ${
        error
          ? 'bg-red-500/15 text-red-700 dark:text-red-300 border-red-500/40 hover:bg-red-500/25'
          : busy
            ? 'bg-slate-500/15 text-slate-600 dark:text-slate-300 border-slate-500/30 cursor-wait'
            : 'bg-muted text-muted-foreground border-border hover:bg-foreground/10'
      }`}
      aria-label="Re-triage incident with AI"
    >
      <span className={busy ? 'animate-spin inline-block' : 'inline-block'}>↻</span>
    </button>
  );
}

/**
 * Inline status pill for the SOC L1 auto-investigator. Shown next to the
 * incident title so analysts can tell at a glance whether the autonomous
 * RCA has already been posted to Sentinel for this incident, is still
 * running, or failed. `null` (no investigation yet) renders nothing - the
 * scheduler kicks them off opportunistically and we don't want a row of
 * "pending" badges flashing for every dashboard refresh.
 */
function AutoInvestigationBadge({
  ai,
}: {
  ai: NonNullable<Incident['autoInvestigation']> | null;
}) {
  if (!ai) return null;
  const finished = ai.finishedAt ? fmtTs(ai.finishedAt) : '';
  const tooltipBase =
    ai.status === 'posted'
      ? `SOC L1 Auto-Investigation posted to Sentinel${finished ? ` at ${finished}` : ''}`
      : ai.status === 'completed'
        ? `Neo analysis completed${finished ? ` at ${finished}` : ''}; Sentinel comment delivery did not succeed${ai.error ? ` (${ai.error})` : ''}`
      : ai.status === 'running'
        ? 'SOC L1 Auto-Investigation in progress'
        : ai.status === 'pending'
          ? 'Queued for SOC L1 Auto-Investigation'
          : ai.status === 'failed'
            ? `SOC L1 Auto-Investigation failed${finished ? ` at ${finished}` : ''} - open the incident for details`
            : 'SOC L1 Auto-Investigation skipped (incident closed)';

  const cls =
    ai.status === 'posted'
      ? 'bg-emerald-500/20 text-emerald-700 dark:text-emerald-300 border-emerald-500/40'
      : ai.status === 'completed'
        ? 'bg-amber-500/20 text-amber-700 dark:text-amber-300 border-amber-500/40'
      : ai.status === 'running' || ai.status === 'pending'
        ? 'bg-sky-500/20 text-sky-700 dark:text-sky-300 border-sky-500/40'
        : ai.status === 'failed'
          ? 'bg-red-500/20 text-red-700 dark:text-red-300 border-red-500/40'
          : 'bg-muted text-muted-foreground border-border';

  const label =
    ai.status === 'posted'
      ? 'L1'
      : ai.status === 'completed'
        ? 'L1✓'
      : ai.status === 'running'
        ? 'L1…'
        : ai.status === 'pending'
          ? 'L1·q'
          : ai.status === 'failed'
            ? 'L1✗'
            : 'L1·skip';

  return (
    <span
      className={`inline-block px-1.5 py-0.5 text-[9px] font-bold rounded border ${cls}`}
      title={tooltipBase}
    >
      {label}
    </span>
  );
}

interface IncidentsTabProps {
  incidents: Incident[];
  severityFilter: string;
  statusFilter: string[];
  onSeverityFilter: (s: string) => void;
  onStatusFilter: (statuses: string[]) => void;
  onIncidentAction: (id: string, action: 'close' | 'assign' | 'status' | 'severity' | 'sync', payload?: ClosePayload | IncidentStatusPayload | IncidentSeverityPayload | Incident) => void;
  customerId?: string;
  onRunKql?: (query: string) => void;
  autoOpenIncidentNumber?: string | null;
  onAutoOpenConsumed?: () => void;
  /**
   * When true, the table/list is hidden and only the auto-opened incident is
   * rendered as a full-page view (used for ?incident=… deep links). If the
   * incident isn't found in the loaded set, a friendly fallback is shown.
   */
  pageMode?: boolean;
  /** Called from the in-page "← Back" button when in pageMode. */
  onExitPageMode?: () => void;
  /**
   * In pageMode, called when the user clicks a related-incident swap so the
   * parent can update the URL/source-of-truth target (otherwise the new
   * incident wouldn't survive a refresh / tenant change).
   */
  onPageNavigate?: (incidentNumber: string) => void;
  /**
   * When true, hide the Actions column entirely (assign / close), and don't
   * pass the run-KQL handler to the modal. Used by the customer portal so
   * read-only users don't see dead/disabled buttons.
   */
  readOnly?: boolean;
  /** Enables the Stage 5D Guardian operational surface inside IncidentOps. */
  showGuardianOperations?: boolean;
}

interface StatusDialogTarget {
  incident: Incident;
  initialStatus?: IncidentStatusPayload['status'];
}

interface SeverityDialogTarget {
  incident: Incident;
  initialSeverity?: IncidentSeverityPayload['severity'];
}

export function IncidentsTab({ incidents, severityFilter, statusFilter, onSeverityFilter, onStatusFilter, onIncidentAction, customerId, onRunKql, autoOpenIncidentNumber, onAutoOpenConsumed, pageMode = false, onExitPageMode, onPageNavigate, readOnly = false, showGuardianOperations = false }: IncidentsTabProps) {
  const { user, canCapability } = useAuth();
  const canAssignIncident = !readOnly && canCapability('incidents', 'assign');
  const canCloseIncident = !readOnly && canCapability('incidents', 'close');
  const canReopenIncident = !readOnly && canCapability('incidents', 'reopen');
  const canChangeIncidentStatus = !readOnly && canCapability('incidents', 'change_status');
  const canChangeIncidentSeverity = !readOnly && canCapability('incidents', 'change_severity');
  const canManageIncidentStatus = canReopenIncident || canChangeIncidentStatus;
  const canRunAiInvestigation = !readOnly && canCapability('incidents', 'run_ai_investigation');
  const canViewEvidence = canCapability('incidents', 'view_evidence');
  const canSyncIncident = canCapability('incidents', 'read');
  const canViewMlScores = canCapability('ml_insights', 'read');
  const canViewGuardianOperations = showGuardianOperations && canCapability('guardian_soc', 'view_guardian_operations');
  const [incidentOpsView, setIncidentOpsView] = useState<'incidents' | 'guardian'>('incidents');
  const { customers } = useCustomers();
  const [extraTenantIds, setExtraCustomerIds] = useState<string[]>([]);
  const [tenantDropdownOpen, setTenantDropdownOpen] = useState(false);
  const tenantDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!canViewGuardianOperations && incidentOpsView === 'guardian') setIncidentOpsView('incidents');
  }, [canViewGuardianOperations, incidentOpsView]);

  useEffect(() => {
    if (!tenantDropdownOpen) return;
    const handler = (e: MouseEvent) => {
      if (tenantDropdownRef.current && !tenantDropdownRef.current.contains(e.target as Node)) setTenantDropdownOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [tenantDropdownOpen]);

  const extraQueries = useQueries({
    queries: extraTenantIds.map(id => ({
      queryKey: ['incidents-tenant', id],
      queryFn: () => apiFetch<{ incidents?: unknown[] }>(`/dashboard-data?customerId=${encodeURIComponent(id)}&days=7`),
      staleTime: 60_000,
      enabled: extraTenantIds.length > 0,
    })),
  });

  const allIncidents = useMemo(() => {
    const base = incidents.map(i => ({ ...i, _customerId: customerId ?? '' }));
    const extra = extraTenantIds.flatMap((id, idx) => {
      const rows = (extraQueries[idx]?.data as { incidents?: { id: string; incidentNumber: string; title: string; severity: string; status: string; assignedTo: string; createdTime: string; lastUpdateTime: string }[] } | undefined)?.incidents ?? [];
      return rows.map(i => ({ ...i, _customerId: id } as import('../data/demoData').Incident & { _customerId: string }));
    });
    return [...base, ...extra];
  }, [incidents, customerId, extraTenantIds, extraQueries]);

  const activeTenantCount = 1 + extraTenantIds.length;
  const showTenantCol = activeTenantCount > 1;

  const toggleExtraTenant = useCallback((id: string) => {
    setExtraCustomerIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  }, []);

  // Click-to-sort for the Created/Updated time columns - toggles direction
  // when the same column is clicked again, defaults to newest-first so
  // analysts land on the most urgent (or, flipped, most stale) incidents.
  const [timeSort, setTimeSort] = useState<{ column: 'created' | 'updated'; dir: 'asc' | 'desc' } | null>(null);
  const toggleTimeSort = useCallback((column: 'created' | 'updated') => {
    setTimeSort((prev) => {
      if (prev?.column === column) return { column, dir: prev.dir === 'desc' ? 'asc' : 'desc' };
      return { column, dir: 'desc' };
    });
  }, []);

  const [hideRedirected, setHideRedirected] = useState(true);
  const [severityDropdownOpen, setSeverityDropdownOpen] = useState(false);
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const [onlyMine, setOnlyMine] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [selectedIncident, setSelectedIncident] = useState<Incident | null>(null);
  // Same-tab incident detail page (breadcrumb-navigable), driven by the
  // `?incident=` URL param but never via window.open - this is what replaced
  // the old "open incident in a new tab" behavior so analysts don't end up
  // with a flood of tabs. Independent of the parent-controlled `pageMode`
  // prop, which still covers a *bookmarked/shared* incident URL loaded fresh.
  const [inlinePageIncidentNumber, setInlinePageIncidentNumber] = useState<string | null>(null);
  const [closeTarget, setCloseTarget] = useState<Incident | null>(null);
  const [statusTarget, setStatusTarget] = useState<StatusDialogTarget | null>(null);
  const [severityTarget, setSeverityTarget] = useState<SeverityDialogTarget | null>(null);
  // Dialog-based assign flow used by the incident detail view (IncidentModal),
  // which has no table row to host the inline dropdown the list uses below.
  // Same backend (/incidents/assignees, /incidents/:id/assign) either way.
  const [assignTarget, setAssignTarget] = useState<Incident | null>(null);
  const [syncingId, setSyncingId] = useState<string | null>(null);
  const [syncError, setSyncError] = useState<string | null>(null);
  // Deep-investigation drawer target. Independent of `selectedIncident`
  // (the IncidentModal) so analysts can have the modal open AND start a
  // deep investigation in parallel without one closing the other.
  const [deepInvestigateTarget, setDeepInvestigateTarget] = useState<Incident | null>(null);
  const [assigningId, setAssigningId] = useState<string | null>(null);
  const [assignValue, setAssignValue] = useState('');
  const [assignLoading, setAssignLoading] = useState(false);
  const [assignError, setAssignError] = useState<string | null>(null);
  const [assignFallbackPrompt, setAssignFallbackPrompt] = useState<string | null>(null);
  const [assignFallbackAllowed, setAssignFallbackAllowed] = useState(false);
  const [tenantUsers, setTenantUsers] = useState<TenantUser[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [filterText, setFilterText] = useState('');
  const [mlSortEnabled, setMlSortEnabled] = useState(false);
  const [mlScores, setMlScores] = useState<MlIncidentScoreRow[]>([]);
  const [mlScoresLoading, setMlScoresLoading] = useState(false);
  const [mlScoresError, setMlScoresError] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const severityDropdownRef = useRef<HTMLDivElement>(null);
  const statusDropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [dropdownPos, setDropdownPos] = useState<{ top: number; left: number; width: number } | null>(null);

  useEffect(() => {
    const h = (e: MouseEvent) => { if (severityDropdownRef.current && !severityDropdownRef.current.contains(e.target as Node)) setSeverityDropdownOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);
  useEffect(() => {
    const h = (e: MouseEvent) => { if (statusDropdownRef.current && !statusDropdownRef.current.contains(e.target as Node)) setStatusDropdownOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);
    useEffect(() => {
    if (!assigningId || !inputRef.current) { setDropdownPos(null); return; }
    const update = () => {
      const r = inputRef.current?.getBoundingClientRect();
      if (r) setDropdownPos({ top: r.bottom + 4, left: r.left, width: Math.max(220, r.width) });
    };
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [assigningId, filterText]);

  useEffect(() => {
    let cancelled = false;
    setUsersLoading(true);
    const qs = customerId ? `?customerId=${encodeURIComponent(customerId)}` : '';
    apiFetch<{ ok: boolean; assignees: TenantUser[] }>(`/incidents/assignees${qs}`).then((response) => {
      if (!cancelled) setTenantUsers(Array.isArray(response.assignees) ? response.assignees : []);
    }).catch(() => { if (!cancelled) setTenantUsers([]); }).finally(() => { if (!cancelled) setUsersLoading(false); });
    return () => { cancelled = true; };
  }, [customerId]);

  useEffect(() => {
    if (!canViewMlScores) { setMlScores([]); return; }
    let cancelled = false;
    setMlScoresLoading(true);
    setMlScoresError(null);
    // Raised alongside the full-backfill ML scoring change (open + closed
    // incidents, no time window) - a single tenant's score history can now
    // comfortably exceed the old 1000-row request.
    const qs = customerId && customerId !== 'default' ? `?customerId=${encodeURIComponent(customerId)}&limit=5000` : '?limit=5000';
    apiFetch<{ ok: boolean; scores: MlIncidentScoreRow[] }>(`/ml-insights/incidents${qs}`).then((response) => {
      if (!cancelled) setMlScores(Array.isArray(response.scores) ? response.scores : []);
    }).catch((err) => {
      if (!cancelled) {
        setMlScores([]);
        setMlScoresError(err instanceof Error ? err.message : String(err));
      }
    }).finally(() => { if (!cancelled) setMlScoresLoading(false); });
    return () => { cancelled = true; };
  }, [customerId, canViewMlScores]);

  const mlScoreMap = useMemo(() => {
    const map = new Map<string, MlIncidentScoreRow>();
    for (const row of mlScores) {
      const keys = [row.incidentNumber, row.incidentId, row.title]
        .map((value) => String(value ?? '').trim().toLowerCase())
        .filter(Boolean);
      for (const key of keys) if (!map.has(key)) map.set(key, row);
    }
    return map;
  }, [mlScores]);

  function getMlScore(incident: Incident): MlIncidentScoreRow | null {
    const keys = [incident.incidentNumber, incident.id, incident.title]
      .map((value) => String(value ?? '').trim().toLowerCase())
      .filter(Boolean);
    for (const key of keys) {
      const match = mlScoreMap.get(key);
      if (match) return match;
    }
    return null;
  }

  const [triageScores, setTriageScores] = useState<Map<string, TriageQueueScoredIncident>>(new Map());
  const [triageScoresLoading, setTriageScoresLoading] = useState(false);

  // Stable key so we only re-score when a field the backend scorer reads
  // actually changes, not on every incidents-array reference change.
  const triageScoreKey = useMemo(
    () =>
      incidents
        .map(i => {
          const tactics = Array.isArray(i.mitreTactics) ? i.mitreTactics.length : 0;
          const ents = Array.isArray(i.entities) ? i.entities.length : 0;
          return `${i.id}:${i.severity}:${i.status}:${i.assignedTo ?? ''}:${i.alertsCount ?? 0}:${i.createdTime ?? ''}:${tactics}:${ents}:${i.title ?? ''}`;
        })
        .join('|'),
    [incidents],
  );

  useEffect(() => {
    let cancelled = false;
    if (incidents.length === 0) { setTriageScores(new Map()); return; }
    setTriageScoresLoading(true);
    // Send only the fields the scorer reads - the full enriched incidents
    // (timeline, alerts, comments, entities[], etc.) for the whole page
    // trivially exceeds Express's body-parser limit (HTTP 413).
    const slim = incidents.map(i => ({
      id: i.id,
      incidentNumber: i.incidentNumber,
      title: i.title,
      severity: i.severity,
      status: i.status,
      createdTime: i.createdTime,
      assignedTo: i.assignedTo,
      alertsCount: i.alertsCount,
      mitreTactics: i.mitreTactics,
      entities: Array.isArray(i.entities) ? new Array(i.entities.length).fill(null) : [],
    }));
    apiFetch<TriageQueueScoreResponse>('/triage/score', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ incidents: slim }),
    })
      .then(r => {
        if (cancelled) return;
        const map = new Map<string, TriageQueueScoredIncident>();
        for (const s of r.scored ?? []) map.set(s.id, s);
        setTriageScores(map);
      })
      .catch(() => { if (!cancelled) setTriageScores(new Map()); })
      .finally(() => { if (!cancelled) setTriageScoresLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [triageScoreKey]);

  const deduped = useMemo(() => {
    const seen = new Set<string>();
    return allIncidents.filter(i => {
      const key = i.incidentNumber || i.id;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [allIncidents]);

  let filtered = deduped;
  if (severityFilter !== 'all') filtered = filtered.filter(i => i.severity === severityFilter);
  if (statusFilter.length > 0) {
    filtered = filtered.filter(i => {
      const es = effectiveStatus(i);
      return statusFilter.some(s => {
        if (s === 'New') return es === 'new';
        if (s === 'Active') return es === 'active';
        if (s === 'Closed') return es === 'closed';
        return false;
      });
    });
  }
  if (hideRedirected) filtered = filtered.filter(i => effectiveStatus(i) !== 'redirected');
  if (onlyMine) {
    const me = (user?.email || '').toLowerCase();
    const myName = (user?.name || '').toLowerCase();
    filtered = filtered.filter(i => {
      const owner = (i.assignedTo || '').toLowerCase();
      if (!owner || owner === 'unassigned') return false;
      // Guard each `includes` with a truthiness check - `''.includes('')` is
      // true, so without the guard an empty email would match every owner.
      const matchEmail = !!me && (owner === me || owner.includes(me));
      const matchName = !!myName && owner.includes(myName);
      return matchEmail || matchName;
    });
  }
  if (searchText.trim()) {
    const q = searchText.trim().toLowerCase();
    filtered = filtered.filter(i =>
      (i.title || '').toLowerCase().includes(q) ||
      String(i.incidentNumber || '').toLowerCase().includes(q) ||
      (i.assignedTo || '').toLowerCase().includes(q) ||
      getProviderLabel(i).toLowerCase().includes(q) ||
      getAlertProviderLabels(i).some((provider) => provider.toLowerCase().includes(q)) ||
      (i.classificationComment || '').toLowerCase().includes(q)
    );
  }
  if (timeSort) {
    const key = timeSort.column === 'created' ? 'createdTime' : 'lastUpdateTime';
    const mul = timeSort.dir === 'desc' ? -1 : 1;
    filtered = [...filtered].sort((a, b) => mul * (new Date(a[key] || 0).getTime() - new Date(b[key] || 0).getTime()));
  } else if (mlSortEnabled && canViewMlScores) {
    filtered = [...filtered].sort((a, b) => (getMlScore(b)?.mlPriorityScore ?? -1) - (getMlScore(a)?.mlPriorityScore ?? -1));
  }

  useEffect(() => {
    if (!autoOpenIncidentNumber) return;
    const match = deduped.find(i => String(i.incidentNumber) === String(autoOpenIncidentNumber) || i.id === autoOpenIncidentNumber);
    if (match) {
      setSelectedIncident(match);
      onAutoOpenConsumed?.();
    }
  }, [autoOpenIncidentNumber, deduped, onAutoOpenConsumed]);

  // Sync the breadcrumb page back to the list when the user hits the
  // browser's Back button (we push a history entry when opening an incident
  // below, so Back should feel like clicking the "Incidents" breadcrumb).
  useEffect(() => {
    if (pageMode) return;
    function onPopState() {
      setInlinePageIncidentNumber(new URLSearchParams(window.location.search).get('incident'));
    }
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [pageMode]);

  function openIncidentPage(incident: Incident) {
    const params = new URLSearchParams(window.location.search);
    params.set('incident', incident.incidentNumber);
    const effectiveCid = (incident as Incident & { _customerId?: string })._customerId || customerId;
    if (effectiveCid) params.set('customer', effectiveCid);
    else params.delete('customer');
    window.history.pushState({}, '', `${window.location.pathname}?${params.toString()}`);
    setInlinePageIncidentNumber(incident.incidentNumber);
  }

  // Swapping to a related incident from within the detail page updates the
  // current history entry in place (mirrors `navigateDeepLinkTarget` in
  // App.tsx) so the back button returns to the list, not through every
  // related incident visited along the way.
  function navigateInlinePage(incidentNumber: string) {
    const params = new URLSearchParams(window.location.search);
    params.set('incident', incidentNumber);
    window.history.replaceState({}, '', `${window.location.pathname}?${params.toString()}`);
    setInlinePageIncidentNumber(incidentNumber);
  }

  function closeIncidentPage() {
    const url = new URL(window.location.href);
    url.searchParams.delete('incident');
    url.searchParams.delete('customer');
    window.history.replaceState({}, '', url.toString());
    setInlinePageIncidentNumber(null);
  }

  function openCloseDialog(incident: Incident) {
    if (!canCloseIncident) return;
    setSelectedIncident(null);
    setCloseTarget(incident);
  }

  function handleClosedConfirm(payload: ClosePayload) {
    if (!closeTarget) return;
    onIncidentAction(closeTarget.id, 'close', payload);
    setCloseTarget(null);
  }

  function openStatusDialog(incident: Incident, initialStatus?: IncidentStatusPayload['status']) {
    const status = effectiveStatus(incident);
    if (status === 'closed' && !canReopenIncident) return;
    if (status !== 'closed' && !canChangeIncidentStatus) return;
    setSelectedIncident(null);
    setStatusTarget({ incident, initialStatus });
  }

  function handleStatusConfirm(payload: IncidentStatusPayload) {
    if (!statusTarget) return;
    onIncidentAction(statusTarget.incident.id, 'status', payload);
    setStatusTarget(null);
  }

  function openSeverityDialog(incident: Incident, initialSeverity?: IncidentSeverityPayload['severity']) {
    if (!canChangeIncidentSeverity) return;
    setSelectedIncident(null);
    setSeverityTarget({ incident, initialSeverity });
  }

  function handleSeverityConfirm(payload: IncidentSeverityPayload) {
    if (!severityTarget) return;
    onIncidentAction(severityTarget.incident.id, 'severity', payload);
    setSeverityTarget(null);
  }

  function openAssignDialog(incident: Incident) {
    if (!canAssignIncident) return;
    setSelectedIncident(null);
    setAssignTarget(incident);
  }

  function handleAssignConfirm(payload: IncidentAssignPayload) {
    if (!assignTarget) return;
    onIncidentAction(assignTarget.id, 'assign', payload);
    setAssignTarget(null);
  }

  function startAssign(incident: Incident) {
    if (!canAssignIncident) return;
    setAssigningId(incident.id);
    setAssignValue('');
    setFilterText('');
    setAssignError(null);
    setAssignFallbackPrompt(null);
    setAssignFallbackAllowed(false);
  }

  function cancelAssign() {
    setAssigningId(null);
    setAssignValue('');
    setFilterText('');
    setAssignError(null);
    setAssignFallbackPrompt(null);
    setAssignFallbackAllowed(false);
  }

  function selectUser(user: TenantUser) {
    setAssignValue(user.userPrincipalName || user.email || '');
    setFilterText(user.displayName);
  }

  async function syncIncidentNow(incident: Incident) {
    if (!canSyncIncident || syncingId) return;
    setSyncingId(incident.id);
    setSyncError(null);
    try {
      const qs = customerId ? `?customerId=${encodeURIComponent(customerId)}` : '';
      const resp = await apiFetch<{ ok: boolean; source: string; incident: Incident; fetchedAt: string }>(
        `/incidents/${encodeURIComponent(incident.id)}/sync${qs}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            armIncidentName: incident.armIncidentName,
            graphIncidentId: incident.graphIncidentId,
          }),
        },
      );
      if (resp.incident) {
        onIncidentAction(incident.id, 'sync', resp.incident);
        setSelectedIncident(prev => prev && prev.id === incident.id ? { ...prev, ...resp.incident } : prev);
      }
    } catch (err) {
      setSyncError(err instanceof Error ? err.message : 'Sync failed');
    } finally {
      setSyncingId(null);
    }
  }

  async function confirmAssign(incident: Incident, useServicePrincipalFallback = false) {
    const trimmed = assignValue.trim() || filterText.trim();
    if (!trimmed) { setAssignError('Select an analyst'); return; }
    setAssignLoading(true);
    setAssignError(null);
    if (!useServicePrincipalFallback) { setAssignFallbackPrompt(null); setAssignFallbackAllowed(false); }
    try {
      const qs = customerId ? `?customerId=${encodeURIComponent(customerId)}` : '';
      await apiFetch(`/incidents/${encodeURIComponent(incident.id)}/assign${qs}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          assignee: trimmed,
          armIncidentName: incident.armIncidentName,
          graphIncidentId: incident.graphIncidentId,
          useServicePrincipalFallback,
        }),
      });
      notifyIncidentExecutionUpdated(incident.id);
      onIncidentAction(incident.id, 'assign', {
        classification: '',
        classificationReason: '',
        classificationComment: '',
        assignee: trimmed,
      });
      setAssigningId(null);
      setAssignValue('');
      setFilterText('');
      setAssignFallbackPrompt(null);
      setAssignFallbackAllowed(false);
    } catch (e) {
      if (e instanceof ApiError && e.status === 409 && e.body?.['error'] === 'delegated_rbac_required') {
        setAssignFallbackPrompt(typeof e.body['message'] === 'string' ? e.body['message'] as string : 'Your user identity could not assign this incident.');
        setAssignFallbackAllowed(e.body?.['canUseServicePrincipalFallback'] === true);
        setAssignError(null);
      } else {
        setAssignError(e instanceof Error ? e.message : String(e));
      }
    } finally {
      setAssignLoading(false);
    }
  }

  const filteredUsers = tenantUsers.filter(u => {
    if (!filterText) return true;
    const q = filterText.toLowerCase();
    return u.displayName.toLowerCase().includes(q) || u.userPrincipalName.toLowerCase().includes(q) || String(u.role ?? '').toLowerCase().includes(q) || String(u.email ?? '').toLowerCase().includes(q);
  });

  const incidentTableColumns: FilterableColumnDef<Incident>[] = [
    ...(showTenantCol ? [{
      key: 'tenant' as const,
      header: 'Tenant',
      value: (incident: Incident) => (incident as Incident & { _customerId?: string })._customerId ?? '',
      render: (incident: Incident) => {
        const cid = (incident as Incident & { _customerId?: string })._customerId;
        const name = customers.find(cu => cu.id === cid)?.name ?? cid ?? '';
        return <span className="truncate max-w-[120px] block text-xs text-muted-foreground" title={name}>{name}</span>;
      },
      cellClassName: 'whitespace-nowrap',
      cellStyle: { verticalAlign: 'middle' },
    }] as FilterableColumnDef<Incident>[] : []),
    {
      key: 'incidentNumber',
      header: 'Incident #',
      value: (incident) => sentinelIncidentNumber(incident),
      render: (incident) => {
        const displayNumber = sentinelIncidentNumber(incident);
        const sentinelUrl = incident.webUrl || `https://security.microsoft.com/incidents/${displayNumber}`;
        return (
          <div className="flex items-center gap-1.5" onClick={e => e.stopPropagation()}>
            <a
              href={sentinelUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 dark:text-blue-400 hover:underline hover:text-blue-700 dark:hover:text-blue-300"
              title="Open in Microsoft Sentinel"
            >
              #{displayNumber}
            </a>
            <button
              type="button"
              onClick={() => openIncidentPage(incident)}
              className="text-muted-foreground/60 hover:text-primary transition-colors"
              aria-label={`Open incident ${displayNumber} details`}
              title="Open incident details"
            >
              <svg aria-hidden="true" className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </button>
          </div>
        );
      },
      cellClassName: 'text-xs font-mono whitespace-nowrap',
      cellStyle: { verticalAlign: 'middle' },
    },
    {
      key: 'triageQueue',
      header: 'Triage Queue',
      value: (incident) => triageScores.get(incident.id)?.score ?? '',
      render: (incident) => {
        const s = triageScores.get(incident.id);
        if (!s) {
          return triageScoresLoading
            ? <span className="text-xs text-muted-foreground/60 italic">scoring…</span>
            : <span className="text-xs text-muted-foreground/60">--</span>;
        }
        return (
          <div title={s.reasons.join(' · ') || 'Triage queue score'}>
            <span className={`text-[10px] font-black px-2 py-1 rounded-full border ${TRIAGE_QUEUE_CATEGORY_CLASS[s.category]}`}>{s.score}</span>
            <div className="mt-1 text-[10px] text-muted-foreground">{s.category}</div>
          </div>
        );
      },
      cellClassName: 'whitespace-nowrap',
    },
    {
      key: 'title',
      header: 'Title',
      value: (incident) => incident.title,
      render: (incident) => (
        <div className="min-w-0">
          <span className="line-clamp-2">{incident.title}</span>
          <div className="mt-1 flex flex-wrap items-center gap-1">
            {typeof incident.dedupCount === 'number' && incident.dedupCount > 1 && (
              <span
                className="inline-block px-1.5 py-0.5 text-[9px] font-bold rounded bg-purple-500/30 text-purple-200 border border-purple-500/50"
                title={`${incident.dedupCount} duplicate incidents collapsed under this one`}
              >
                ×{incident.dedupCount}
              </span>
            )}
            <AutoInvestigationBadge ai={incident.autoInvestigation ?? null} />
            <TriageVerdictBadge triage={incident.triage ?? null} />
            {(() => {
              const score = getMlScore(incident);
              if (!score) return null;
              const scoreClass = score.mlPriorityScore >= 85
                ? 'bg-red-500/15 text-red-700 dark:text-red-300 border-red-500/40'
                : score.mlPriorityScore >= 70
                  ? 'bg-orange-500/15 text-orange-700 dark:text-orange-300 border-orange-500/40'
                  : score.mlPriorityScore >= 45
                    ? 'bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/40'
                    : 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/40';
              return (
                <span
                  className={`inline-flex items-center text-[10px] font-black px-1.5 py-0.5 rounded border ${scoreClass}`}
                  title={score.recommendedAction || 'ML priority score'}
                >
                  ML {score.mlPriorityScore}/100
                </span>
              );
            })()}
            {((incident as Incident & { _customerId?: string })._customerId || customerId) && (
              <ReTriageButton
                incidentId={incident.id}
                customerId={(incident as Incident & { _customerId?: string })._customerId || customerId}
                triage={incident.triage ?? null}
                disabled={readOnly}
              />
            )}
            <SlaBreachBadge customerId={(incident as Incident & { _customerId?: string })._customerId || customerId} severity={incident.severity} status={incident.status} createdAt={incident.createdTime} incidentNumber={incident.incidentNumber} incidentTitle={incident.title} />
          </div>
        </div>
      ),
      cellClassName: 'font-medium text-foreground max-w-[280px]',
    },
    {
      key: 'severity',
      header: 'Severity',
      value: (incident) => incident.severity,
      cellStyle: { verticalAlign: 'middle' },
      render: (incident) => (
        <>
          <span className={`text-xs font-semibold px-2 py-1 rounded ${getSeverityClass(incident.severity)}`}>{incident.severity}</span>
          <SlaBreachBadge customerId={(incident as Incident & { _customerId?: string })._customerId || customerId} severity={incident.severity} status={incident.status} createdAt={incident.createdTime} incidentNumber={incident.incidentNumber} incidentTitle={incident.title} />
        </>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      value: (incident) => getStatusLabel(incident),
      cellStyle: { verticalAlign: 'middle' },
      render: (incident) => <span className={`text-xs font-semibold px-2 py-1 rounded ${getStatusClass(incident)}`}>{getStatusLabel(incident)}</span>,
    },
    {
      key: 'incidentProvider',
      header: 'Incident Provider',
      value: (incident) => getProviderLabel(incident),
      headerClassName: 'hidden xl:table-cell',
      cellClassName: 'hidden xl:table-cell',
      cellStyle: { verticalAlign: 'middle' },
      render: (incident) => {
        const provider = getProviderLabel(incident);
        return <span className={`text-[10px] font-semibold px-2 py-1 rounded-full border whitespace-nowrap ${getProviderClass(provider)}`}>{provider}</span>;
      },
    },
    {
      key: 'alertProvider',
      header: 'Alert Provider',
      value: (incident) => getAlertProviderLabels(incident).join(', '),
      headerClassName: 'hidden xl:table-cell',
      cellClassName: 'hidden xl:table-cell',
      cellStyle: { verticalAlign: 'middle' },
      render: (incident) => {
        const providers = getAlertProviderLabels(incident);
        if (providers.length === 0) return <span className="text-xs text-muted-foreground/60">--</span>;
        // Cap at 2 visible pills and fold the rest (including whatever
        // getAlertProviderLabels() already truncated) into a single "+N"
        // badge - keeps this cell single-line instead of wrapping onto a
        // second/third row and stretching the whole table row taller.
        const visible = providers.slice(0, 2);
        const hiddenCount = (incident.alertProviderNames?.length || providers.length) - visible.length;
        return (
          <div className="flex flex-nowrap items-center gap-1 max-w-[180px] overflow-hidden">
            {visible.map((provider) => (
              <span
                key={`${incident.id}-alert-provider-${provider}`}
                className={`text-[10px] font-semibold px-2 py-1 rounded-full border whitespace-nowrap shrink-0 ${getProviderClass(provider)}`}
                title={`SecurityAlert.ProductName: ${provider}`}
              >
                {provider}
              </span>
            ))}
            {hiddenCount > 0 && (
              <span
                className="text-[10px] font-semibold px-2 py-1 rounded-full border whitespace-nowrap shrink-0 bg-muted text-muted-foreground border-border"
                title={(incident.alertProviderNames || []).slice(visible.length).join(', ')}
              >
                +{hiddenCount}
              </span>
            )}
          </div>
        );
      },
    },
    {
      key: 'owner',
      header: 'Owner',
      value: (incident) => incident.assignedTo && incident.assignedTo !== 'Unassigned' ? incident.assignedTo : 'Unassigned',
      headerClassName: 'hidden md:table-cell',
      cellClassName: 'text-xs text-muted-foreground hidden md:table-cell max-w-[140px] truncate',
      cellStyle: { verticalAlign: 'middle' },
      render: (incident) => incident.assignedTo && incident.assignedTo !== 'Unassigned'
        ? incident.assignedTo
        : <span className="italic text-muted-foreground/60">Unassigned</span>,
    },
    {
      key: 'created',
      header: 'Created',
      sortLabel: (
        <span className="inline-flex items-center gap-1">
          Created Time
          {timeSort?.column === 'created' && <span aria-hidden>{timeSort.dir === 'desc' ? '↓' : '↑'}</span>}
        </span>
      ),
      onSort: () => toggleTimeSort('created'),
      value: (incident) => incident.createdTime,
      headerClassName: 'hidden lg:table-cell',
      cellClassName: 'text-xs text-muted-foreground hidden lg:table-cell whitespace-nowrap',
      cellStyle: { verticalAlign: 'middle' },
      render: (incident) => (
        <span title={incident.createdTime ? formatTimeAgo(incident.createdTime) : undefined}>
          {incident.createdTime ? fmtTs(incident.createdTime) : '--'}
        </span>
      ),
    },
    {
      key: 'updated',
      header: 'Updated',
      sortLabel: (
        <span className="inline-flex items-center gap-1">
          Updated Time
          {timeSort?.column === 'updated' && <span aria-hidden>{timeSort.dir === 'desc' ? '↓' : '↑'}</span>}
        </span>
      ),
      onSort: () => toggleTimeSort('updated'),
      value: (incident) => incident.lastUpdateTime,
      headerClassName: 'hidden lg:table-cell',
      cellClassName: 'text-xs text-muted-foreground hidden lg:table-cell whitespace-nowrap',
      cellStyle: { verticalAlign: 'middle' },
      render: (incident) => (
        <span title={incident.lastUpdateTime ? formatTimeAgo(incident.lastUpdateTime) : undefined}>
          {incident.lastUpdateTime ? fmtTs(incident.lastUpdateTime) : '--'}
        </span>
      ),
    },
    ...((canAssignIncident || canCloseIncident || canManageIncidentStatus || canChangeIncidentSeverity || canRunAiInvestigation || canSyncIncident) ? [{
      key: 'actions',
      header: 'Actions',
      value: () => '',
      filterEnabled: false,
      cellClassName: 'min-w-[200px]',
      render: (incident: Incident) => (
        <div onClick={e => e.stopPropagation()}>
          {assigningId === incident.id ? (
            <div className="flex flex-col gap-1 relative" ref={dropdownRef}>
              <div className="flex items-center gap-1">
                <div className="relative">
                  <input
                    ref={inputRef}
                    type="text"
                    value={filterText}
                    onChange={e => { setFilterText(e.target.value); setAssignValue(''); }}
                    onKeyDown={e => {
                      if (e.key === 'Enter') confirmAssign(incident);
                      if (e.key === 'Escape') cancelAssign();
                    }}
                    placeholder={usersLoading ? 'Loading…' : tenantUsers.length === 0 ? 'No eligible SOC assignees' : 'Select SOC analyst…'}
                    disabled={assignLoading}
                    autoFocus
                    className="text-xs border border-border rounded px-2 py-1 bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-primary w-[180px] placeholder:text-muted-foreground/50"
                  />
                  {!assignValue && dropdownPos && createPortal(
                    <div
                      style={{ position: 'fixed', top: dropdownPos.top, left: dropdownPos.left, width: dropdownPos.width, zIndex: 9999 }}
                      className="max-h-[260px] overflow-y-auto bg-card border border-border rounded-lg shadow-2xl"
                    >
                      {usersLoading && <div className="px-3 py-2 text-xs italic text-muted-foreground">Loading users…</div>}
                      {!usersLoading && tenantUsers.length === 0 && (
                        <div className="px-3 py-2 text-xs italic text-muted-foreground">
                          No eligible SOC assignees. Check Masters → SOC Assignment Master, Users Master customer access, and incidents.assign permission.
                        </div>
                      )}
                      {!usersLoading && filteredUsers.slice(0, 25).map(user => (
                        <button
                          key={user.userPrincipalName}
                          onMouseDown={e => { e.preventDefault(); selectUser(user); }}
                          className="w-full text-left px-3 py-2 text-xs hover:bg-muted transition-colors flex flex-col"
                        >
                          <span className="font-medium text-foreground">{user.displayName}</span>
                          <span className="text-muted-foreground text-[10px] truncate">{user.userPrincipalName}</span>
                          <span className="text-muted-foreground text-[10px] truncate">{user.role ? `${user.role}` : 'SOC roster'}{user.lighthouseVerified ? ' · Lighthouse verified' : ''}{user.autoAssign ? ' · Auto-assign ready' : ''}</span>
                        </button>
                      ))}
                      {!usersLoading && filteredUsers.length > 25 && (
                        <div className="px-3 py-1.5 text-[10px] text-muted-foreground border-t border-border bg-muted/30">
                          Showing 25 of {filteredUsers.length} eligible SOC assignees. Type to filter.
                        </div>
                      )}
                      {!usersLoading && tenantUsers.length > 0 && filteredUsers.length === 0 && (
                        <div className="px-3 py-2 text-xs italic text-muted-foreground">No matches for "{filterText}"</div>
                      )}
                    </div>,
                    document.body
                  )}
                </div>
                <button
                  onClick={() => confirmAssign(incident)}
                  disabled={assignLoading}
                  title="Confirm"
                  className="text-xs bg-primary text-primary-foreground border border-primary px-2 py-1 rounded hover:bg-primary/90 transition-colors disabled:opacity-50 flex items-center gap-1"
                >
                  {assignLoading
                    ? <span className="w-3 h-3 border border-white/40 border-t-white rounded-full animate-spin inline-block" />
                    : 'OK'}
                </button>
                <button
                  onClick={cancelAssign}
                  disabled={assignLoading}
                  title="Cancel"
                  className="text-xs border border-border text-muted-foreground px-2 py-1 rounded hover:bg-muted transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
              </div>
              {assignError && (
                <span className="text-xs text-red-500 truncate max-w-[200px]">{assignError}</span>
              )}
              {assignFallbackPrompt && (
                <div className="max-w-[260px] rounded border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/40 p-2 text-[10px] text-amber-900 dark:text-amber-100">
                  <div className="mb-1">{assignFallbackPrompt}</div>
                  <div className="flex gap-1">
                    {assignFallbackAllowed && (
                      <button type="button" onClick={() => void confirmAssign(incident, true)} disabled={assignLoading} className="rounded bg-amber-600 px-2 py-1 text-white font-semibold">Use Service Principal</button>
                    )}
                    <button type="button" onClick={() => { setAssignFallbackPrompt(null); setAssignFallbackAllowed(false); }} disabled={assignLoading} className="rounded border border-amber-400 px-2 py-1">Cancel</button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="flex gap-1 flex-wrap">
              {canSyncIncident && (
                <button
                  onClick={() => syncIncidentNow(incident)}
                  disabled={syncingId === incident.id}
                  title="Fetch this incident directly from Microsoft Sentinel now, bypassing Log Analytics ingestion delay"
                  className="text-xs bg-slate-50 dark:bg-slate-950 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-700 px-2 py-1 rounded hover:bg-slate-100 dark:hover:bg-slate-900 transition-colors disabled:opacity-50"
                >
                  {syncingId === incident.id ? 'Syncing…' : 'Sync'}
                </button>
              )}
              {effectiveStatus(incident) !== 'closed' ? (
                <>
                  {canAssignIncident && (
                    <button onClick={() => startAssign(incident)} className="text-xs bg-blue-50 dark:bg-blue-950 text-blue-600 dark:text-blue-400 border border-blue-200 dark:border-blue-800 px-2 py-1 rounded hover:bg-blue-100 dark:hover:bg-blue-900 transition-colors">Assign</button>
                  )}
                  {canCloseIncident && (
                    <button onClick={() => openCloseDialog(incident)} className="text-xs bg-green-50 dark:bg-green-950 text-green-600 dark:text-green-400 border border-green-200 dark:border-green-800 px-2 py-1 rounded hover:bg-green-100 dark:hover:bg-green-900 transition-colors">Close</button>
                  )}
                  {canChangeIncidentStatus && (
                    <button onClick={() => openStatusDialog(incident)} className="text-xs bg-sky-50 dark:bg-sky-950 text-sky-600 dark:text-sky-400 border border-sky-200 dark:border-sky-800 px-2 py-1 rounded hover:bg-sky-100 dark:hover:bg-sky-900 transition-colors">Status</button>
                  )}
                  {canChangeIncidentSeverity && (
                    <button onClick={() => openSeverityDialog(incident)} className="text-xs bg-amber-50 dark:bg-amber-950 text-amber-600 dark:text-amber-400 border border-amber-200 dark:border-amber-800 px-2 py-1 rounded hover:bg-amber-100 dark:hover:bg-amber-900 transition-colors">Severity</button>
                  )}
                  {customerId && canRunAiInvestigation && user?.role !== 'soc_l1' && (
                    <button onClick={() => setDeepInvestigateTarget(incident)} title="Run a Tier-2/Tier-3 deep investigation (SentinelIR-Agent)" className="text-xs bg-purple-50 dark:bg-purple-950 text-purple-600 dark:text-purple-400 border border-purple-200 dark:border-purple-800 px-2 py-1 rounded hover:bg-purple-100 dark:hover:bg-purple-900 transition-colors">Deep Analysis</button>
                  )}
                </>
              ) : (
                <>
                  {canReopenIncident && (
                    <button onClick={() => openStatusDialog(incident)} className="text-xs bg-blue-50 dark:bg-blue-950 text-blue-600 dark:text-blue-400 border border-blue-200 dark:border-blue-800 px-2 py-1 rounded hover:bg-blue-100 dark:hover:bg-blue-900 transition-colors">Reopen</button>
                  )}
                  {canChangeIncidentSeverity && (
                    <button onClick={() => openSeverityDialog(incident)} className="text-xs bg-amber-50 dark:bg-amber-950 text-amber-600 dark:text-amber-400 border border-amber-200 dark:border-amber-800 px-2 py-1 rounded hover:bg-amber-100 dark:hover:bg-amber-900 transition-colors">Severity</button>
                  )}
                  {incident.classification && incident.classification !== 'unknown' ? (
                    <span className="text-xs text-muted-foreground italic">
                      {String(incident.classification).replace(/([A-Z])/g, ' $1').trim()}
                      {incident.determination && incident.determination !== 'unknown' && ` · ${String(incident.determination).replace(/([A-Z])/g, ' $1').trim()}`}
                    </span>
                  ) : !canReopenIncident ? (
                    <span className="text-xs text-muted-foreground italic">Closed</span>
                  ) : null}
                </>
              )}
            </div>
          )}
        </div>
      ),
    }] : []),
    {
      key: 'comments',
      header: 'Comments',
      value: (incident) => incident.classificationComment || '',
      cellClassName: 'max-w-[220px]',
      render: (incident) => (
        <div onClick={e => e.stopPropagation()}>
          {incident.classificationComment ? (
            <div className="text-xs text-foreground/80 leading-relaxed line-clamp-2 cursor-default" title={incident.classificationComment}>{incident.classificationComment}</div>
          ) : (
            <span className="text-xs text-muted-foreground/40 italic">-</span>
          )}
        </div>
      ),
    },
  ];

  const incidentOpsSwitcher = canViewGuardianOperations ? (
    <div className="mb-4 flex items-center gap-1 rounded-xl border border-border bg-card p-1 shadow-sm">
      <button
        type="button"
        onClick={() => setIncidentOpsView('incidents')}
        className={`rounded-lg px-4 py-2 text-xs font-bold transition-colors ${incidentOpsView === 'incidents' ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted-foreground hover:bg-muted hover:text-foreground'}`}
      >
        Active Incidents
      </button>
      <button
        type="button"
        onClick={() => setIncidentOpsView('guardian')}
        className={`rounded-lg px-4 py-2 text-xs font-bold transition-colors ${incidentOpsView === 'guardian' ? 'bg-violet-500 text-white shadow-sm' : 'text-muted-foreground hover:bg-muted hover:text-foreground'}`}
      >
        Guardian
      </button>
    </div>
  ) : null;

  // ── Deep-link / page mode: render just the (auto-)opened incident as a
  //    full page (no table, no overlay). Falls back gracefully if the
  //    incident can't be found in the loaded set yet (still loading or
  //    filtered out). Covers two cases:
  //    - `pageMode` (parent-controlled): a bookmarked/shared `?incident=…`
  //      URL opened directly, skipping the dashboard chrome entirely.
  //    - `inlinePageIncidentNumber` (local state): an analyst clicked a row
  //      in the list below - same tab, no reload, breadcrumb back to list.
  const showIncidentPage = pageMode || inlinePageIncidentNumber !== null;
  if (showIncidentPage) {
    const targetNumber = pageMode ? (autoOpenIncidentNumber ?? '') : (inlinePageIncidentNumber ?? '');
    // Single source of truth: always re-resolve from `deduped` using the
    // current `targetNumber`. This prevents a stale `selectedIncident` from a
    // wrong-tenant auto-open from sticking after the user switches tenant (or
    // after `pendingCustomerParam` finishes loading the right one).
    const target = targetNumber
      ? (deduped.find(i => String(i.incidentNumber) === String(targetNumber) || i.id === String(targetNumber)) ?? null)
      : null;
    const backToList = () => (pageMode ? onExitPageMode?.() : closeIncidentPage());
    return (
      <div className="slide-in">
        <Breadcrumb className="mb-3">
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink
                href="#"
                onClick={(e) => { e.preventDefault(); backToList(); }}
                className="font-semibold text-primary"
              >
                Incidents
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage className="font-mono">#{target ? sentinelIncidentNumber(target) : targetNumber}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
        {target ? (
          <IncidentModal
            fullPage
            incident={target}
            customerId={(target as Incident & { _customerId?: string })._customerId || customerId}
            mlScore={canViewMlScores ? getMlScore(target) : null}
            onClose={backToList}
            onCloseIncident={canCloseIncident ? () => openCloseDialog(target) : () => undefined}
            onSeverityIncident={canChangeIncidentSeverity ? (severity) => openSeverityDialog(target, severity) : undefined}
            onStatusIncident={canManageIncidentStatus ? (status) => openStatusDialog(target, status) : undefined}
            onAssignIncident={canAssignIncident ? () => openAssignDialog(target) : undefined}
            onSyncIncident={canSyncIncident ? () => syncIncidentNow(target) : undefined}
            syncBusy={syncingId === target.id}
            onRunKql={(canViewEvidence || canRunAiInvestigation) ? onRunKql : undefined}
            readOnly={readOnly}
            onOpenIncident={(num) => {
              // Update the source-of-truth (URL + auto-open state) so
              // navigation survives refresh / tenant switch.
              if (pageMode) onPageNavigate?.(String(num));
              else navigateInlinePage(String(num));
            }}
          />
        ) : (
          <div className="bg-card rounded-xl shadow-xl border border-border max-w-2xl mx-auto p-8 text-center">
            <p className="text-xs font-mono text-muted-foreground mb-2">Incident #{targetNumber}</p>
            <h2 className="text-lg font-bold text-foreground mb-3">Loading incident…</h2>
            <p className="text-sm text-muted-foreground mb-4">
              If this takes more than a few seconds, the incident may be outside the current time window or belong to a different customer.
            </p>
            <button
              onClick={backToList}
              className="text-xs font-semibold border border-border bg-muted/50 text-foreground hover:bg-muted px-4 py-2 rounded-lg transition-colors"
            >
              ← Back to dashboard
            </button>
          </div>
        )}
        {closeTarget && canCloseIncident && (
          <CloseIncidentDialog
            incident={closeTarget}
            customerId={(closeTarget as Incident & { _customerId?: string })._customerId || customerId}
            onCancel={() => setCloseTarget(null)}
            onClosed={handleClosedConfirm}
          />
        )}
        {statusTarget && canManageIncidentStatus && (
          <IncidentStatusDialog
            incident={statusTarget.incident}
            customerId={(statusTarget.incident as Incident & { _customerId?: string })._customerId || customerId}
            initialStatus={statusTarget.initialStatus}
            onCancel={() => setStatusTarget(null)}
            onUpdated={handleStatusConfirm}
          />
        )}
        {assignTarget && canAssignIncident && (
          <IncidentAssignDialog
            incident={assignTarget}
            customerId={(assignTarget as Incident & { _customerId?: string })._customerId || customerId}
            onCancel={() => setAssignTarget(null)}
            onUpdated={handleAssignConfirm}
          />
        )}
      </div>
    );
  }

  if (canViewGuardianOperations && incidentOpsView === 'guardian') {
    return (
      <div className="slide-in">
        {incidentOpsSwitcher}
        <GuardianOperationsPanel customerId={customerId} />
      </div>
    );
  }

  return (
    <div className="slide-in">
      {incidentOpsSwitcher}
      <div className="bg-card rounded-xl shadow p-5">
        <div className="flex flex-wrap gap-3 items-center justify-between mb-4">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-semibold text-foreground">
              Recent Security Incidents
              <span className="ml-2 text-sm font-normal text-muted-foreground">({filtered.length} shown)</span>
            </h3>
          </div>
          <div className="flex flex-wrap gap-4 items-center">
            <div className="relative w-full sm:w-auto">
              <input
                type="search"
                value={searchText}
                onChange={e => setSearchText(e.target.value)}
                placeholder="Search incidents…"
                aria-label="Search incidents"
                className="text-xs border border-border rounded-full pl-7 pr-7 py-1.5 bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-primary w-full sm:w-[200px] placeholder:text-muted-foreground/60"
              />
              <svg aria-hidden="true" className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground/60 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M11 19a8 8 0 100-16 8 8 0 000 16z" />
              </svg>
              {searchText && (
                <button
                  onClick={() => setSearchText('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground/60 hover:text-foreground text-sm leading-none"
                  aria-label="Clear search"
                  title="Clear search"
                >
                  ×
                </button>
              )}
            </div>
            {customers.length > 1 && (
              <div className="relative flex items-center gap-1.5" ref={tenantDropdownRef}>
                <span className="text-xs font-semibold text-muted-foreground">Tenants:</span>
                <button
                  onClick={() => setTenantDropdownOpen(v => !v)}
                  className="px-2.5 py-1 rounded-full text-xs font-semibold border border-border text-muted-foreground hover:border-primary hover:text-primary transition-all flex items-center gap-1"
                >
                  {activeTenantCount === 1 ? (customers.find(c => c.id === customerId)?.name ?? 'Current tenant') : `${activeTenantCount} tenants`}
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7"/></svg>
                </button>
                {tenantDropdownOpen && (
                  <div className="absolute left-0 top-full mt-1 z-50 min-w-[220px] rounded-xl border border-border bg-popover shadow-xl p-2 flex flex-col gap-1">
                    {customers.map(cu => {
                      const isCurrent = cu.id === customerId;
                      const isExtra = extraTenantIds.includes(cu.id);
                      const checked = isCurrent || isExtra;
                      return (
                        <label key={cu.id} className={`flex items-center gap-2 px-2 py-1.5 rounded-lg cursor-pointer text-xs hover:bg-muted/50 ${isCurrent ? 'opacity-60' : ''}`}>
                          <input type="checkbox" checked={checked} disabled={isCurrent} onChange={() => !isCurrent && toggleExtraTenant(cu.id)} className="cursor-pointer" />
                          <span className="truncate max-w-[160px]" title={cu.name}>{cu.name}</span>
                          {isCurrent && <span className="ml-auto text-[10px] text-muted-foreground">current</span>}
                          {isExtra && extraQueries[extraTenantIds.indexOf(cu.id)]?.isFetching && <span className="ml-auto text-[10px] text-muted-foreground">loading…</span>}
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            <div className="relative" ref={severityDropdownRef}>
              <button
                onClick={() => setSeverityDropdownOpen(v => !v)}
                className="flex items-center gap-1.5 text-xs font-semibold border border-border rounded-full px-3 py-1.5 bg-background text-foreground hover:border-primary hover:text-primary transition-all"
              >
                <span className="text-muted-foreground mr-1">Severity:</span>
                {severityFilter === 'all' ? 'All' : severityFilter}
                <svg className="w-3 h-3 ml-1 opacity-60" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
              </button>
              {severityDropdownOpen && (
                <div className="absolute left-0 top-full mt-1 z-50 min-w-[160px] bg-card border border-border rounded-lg shadow-xl overflow-hidden">
                  {['all', 'Critical', 'High', 'Medium', 'Low', 'Informational'].map(s => (
                    <button key={s} onClick={() => { onSeverityFilter(s); setSeverityDropdownOpen(false); }}
                      className={`w-full text-left flex items-center gap-2 px-3 py-2 text-xs hover:bg-muted transition-colors ${severityFilter === s ? 'font-bold text-primary' : 'text-foreground'}`}
                    >
                      {severityFilter === s
                        ? <svg className="w-3 h-3 text-primary flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                        : <span className="w-3 h-3 flex-shrink-0" />}
                      {s === 'all' ? 'All severities' : s}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="relative" ref={statusDropdownRef}>
              <button
                onClick={() => setStatusDropdownOpen(v => !v)}
                className="flex items-center gap-1.5 text-xs font-semibold border border-border rounded-full px-3 py-1.5 bg-background text-foreground hover:border-primary hover:text-primary transition-all"
              >
                <span className="text-muted-foreground mr-1">Status:</span>
                {statusFilter.length === 0 ? 'All' : statusFilter.length === 1
                  ? (statusFilter[0] === 'Active' ? 'In Progress' : statusFilter[0] === 'Closed' ? 'Resolved' : statusFilter[0])
                  : `${statusFilter.length} selected`}
                <svg className="w-3 h-3 ml-1 opacity-60" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
              </button>
              {statusDropdownOpen && (
                <div className="absolute left-0 top-full mt-1 z-50 min-w-[160px] bg-card border border-border rounded-lg shadow-xl overflow-hidden">
                  <button onClick={() => { onStatusFilter([]); setStatusDropdownOpen(false); }}
                    className={`w-full text-left flex items-center gap-2 px-3 py-2 text-xs hover:bg-muted transition-colors ${statusFilter.length === 0 ? 'font-bold text-primary' : 'text-foreground'}`}
                  >
                    {statusFilter.length === 0
                      ? <svg className="w-3 h-3 text-primary flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                      : <span className="w-3 h-3 flex-shrink-0" />}
                    All statuses
                  </button>
                  {([{v:'New',l:'New'},{v:'Active',l:'In Progress'},{v:'Closed',l:'Resolved'}] as {v:string;l:string}[]).map(({v,l}) => (
                    <button key={v}
                      onClick={() => { const next = statusFilter.includes(v) ? statusFilter.filter(x => x !== v) : [...statusFilter, v]; onStatusFilter(next); }}
                      className="w-full text-left flex items-center gap-2 px-3 py-2 text-xs hover:bg-muted transition-colors text-foreground"
                    >
                      <span className={`w-3.5 h-3.5 border rounded flex-shrink-0 flex items-center justify-center ${statusFilter.includes(v) ? 'bg-primary border-primary' : 'border-border'}`}>
                        {statusFilter.includes(v) && <svg className="w-2.5 h-2.5 text-primary-foreground" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>}
                      </span>
                      {l}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <label className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer select-none">
              <input type="checkbox" checked={hideRedirected} onChange={e => setHideRedirected(e.target.checked)} className="cursor-pointer" />
              Hide Redirected
            </label>
            {(canAssignIncident || canCloseIncident || canManageIncidentStatus || canChangeIncidentSeverity || canRunAiInvestigation || canSyncIncident) && (user?.email || user?.name) && (
              <label
                className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer select-none"
                title="Show only incidents assigned to you"
              >
                <input type="checkbox" checked={onlyMine} onChange={e => setOnlyMine(e.target.checked)} className="cursor-pointer" />
                My incidents
              </label>
            )}
          </div>
          {canViewMlScores && (
            <div className="mt-3 flex flex-wrap items-center gap-2 rounded-lg border border-violet-200 bg-violet-50 px-3 py-2 text-xs text-violet-800 dark:border-violet-900 dark:bg-violet-950/30 dark:text-violet-200">
              <label className="flex items-center gap-1.5 font-semibold cursor-pointer select-none">
                <input type="checkbox" checked={mlSortEnabled} onChange={(event) => setMlSortEnabled(event.target.checked)} />
                Sort by ML Priority
              </label>
              <span className="text-violet-700/80 dark:text-violet-200/80">{mlScoresLoading ? 'Loading ML scores…' : `${mlScores.length} cached ML score(s)`}</span>
              {mlScoresError && <span className="text-amber-700 dark:text-amber-200">ML score cache unavailable: {mlScoresError}</span>}
            </div>
          )}
        </div>

        {syncError && (
          <div className="mb-3 rounded-lg border border-amber-300 bg-amber-50 text-amber-800 px-3 py-2 text-sm dark:bg-amber-950/40 dark:text-amber-200 dark:border-amber-800">
            Microsoft Sentinel sync failed: {syncError}
          </div>
        )}

        <FilterableColumnTable
          columns={incidentTableColumns}
          rows={filtered}
          storageKey="kloudsecure:incidents:recent-security-incidents:columns"
          rowKey={(incident) => incident.id}
          rowClassName={() => 'border-b border-border hover:bg-muted/50 transition-colors cursor-pointer'}
          onRowClick={(incident) => {
            if (assigningId === incident.id) return;
            openIncidentPage(incident);
          }}
          emptyMessage="No incidents match the current filters"
          maxHeightClassName="max-h-[500px]"
          tableClassName="w-full text-sm"
        />
      </div>

      {selectedIncident && (
        <IncidentModal
          incident={selectedIncident}
          customerId={(selectedIncident as Incident & { _customerId?: string })._customerId || customerId}
          mlScore={canViewMlScores ? getMlScore(selectedIncident) : null}
          onClose={() => setSelectedIncident(null)}
          onCloseIncident={canCloseIncident ? () => openCloseDialog(selectedIncident) : () => setSelectedIncident(null)}
          onStatusIncident={canManageIncidentStatus ? (status) => openStatusDialog(selectedIncident, status) : undefined}
          onSeverityIncident={canChangeIncidentSeverity ? (severity) => openSeverityDialog(selectedIncident, severity) : undefined}
          onAssignIncident={canAssignIncident ? () => openAssignDialog(selectedIncident) : undefined}
          onSyncIncident={canSyncIncident ? () => syncIncidentNow(selectedIncident) : undefined}
          syncBusy={syncingId === selectedIncident.id}
          onRunKql={(canViewEvidence || canRunAiInvestigation) ? onRunKql : undefined}
          readOnly={readOnly}
          onOpenIncident={(num) => {
            const target = deduped.find(i => String(i.incidentNumber) === String(num) || i.id === String(num));
            if (target) setSelectedIncident(target);
          }}
        />
      )}

      {closeTarget && canCloseIncident && (
        <CloseIncidentDialog
          incident={closeTarget}
          customerId={(closeTarget as Incident & { _customerId?: string })._customerId || customerId}
          onCancel={() => setCloseTarget(null)}
          onClosed={handleClosedConfirm}
        />
      )}

      {statusTarget && canManageIncidentStatus && (
        <IncidentStatusDialog
          incident={statusTarget.incident}
          customerId={(statusTarget.incident as Incident & { _customerId?: string })._customerId || customerId}
          initialStatus={statusTarget.initialStatus}
          onCancel={() => setStatusTarget(null)}
          onUpdated={handleStatusConfirm}
        />
      )}

      {severityTarget && canChangeIncidentSeverity && (
        <IncidentSeverityDialog
          incident={severityTarget.incident}
          customerId={(severityTarget.incident as Incident & { _customerId?: string })._customerId || customerId}
          initialSeverity={severityTarget.initialSeverity}
          onCancel={() => setSeverityTarget(null)}
          onUpdated={handleSeverityConfirm}
        />
      )}

      {assignTarget && canAssignIncident && (
        <IncidentAssignDialog
          incident={assignTarget}
          customerId={(assignTarget as Incident & { _customerId?: string })._customerId || customerId}
          onCancel={() => setAssignTarget(null)}
          onUpdated={handleAssignConfirm}
        />
      )}

      {deepInvestigateTarget && customerId && canRunAiInvestigation && (
        <DeepInvestigationDrawer
          incident={deepInvestigateTarget}
          customerId={(deepInvestigateTarget as Incident & { _customerId?: string })._customerId || customerId}
          readOnly={readOnly}
          onClose={() => setDeepInvestigateTarget(null)}
        />
      )}
    </div>
  );
}
